// Liquid preloads both dependencies in parallel, with Shopify's versioned asset URLs.
const productScript = document.querySelector('script[data-ucp-option-picker-url]');
const [{ savingsPercent, validateArtwork, deliveryWindows, publishedReviews }, { ProductOptionPicker }, {productConfiguration, selectBatch, nativeQuote, orderQuantity, normalizeOrderQuantity, unitPrice}] = await Promise.all([
  import(productScript?.dataset.ucpModelUrl || './usayg-custom-model.js'),
  import(productScript?.dataset.ucpOptionPickerUrl || './usayg-option-picker.js'),
  import(productScript?.dataset.ucpConfigModelUrl || './usayg-product-config.js'),
]);

const element = (tag, className, text) => {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
};

if (!customElements.get('usayg-custom-product')) {
  customElements.define('usayg-custom-product', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.controller = new AbortController();
      this.listen = (node, event, handler) => node?.addEventListener(event, handler, { signal: this.controller.signal });
      this.form = this.querySelector('.ucp-order-form');
      this.addButton = this.querySelector('[data-ucp-add]');
      this.fileInput = this.querySelector('[data-ucp-artwork]');
      this.later = this.querySelector('[data-ucp-artwork-later]');
      this.busy = false;
      this.verified = null;
      this.requestVersion = 0;
      this.priceCache = new Map();
      this.setupTabs();
      try {
        this.config = productConfiguration(JSON.parse(this.querySelector('[data-ucp-config]').textContent), JSON.parse(this.querySelector('[data-ucp-native]').textContent), JSON.parse(this.querySelector('[data-ucp-artwork-settings]')?.textContent || '{}'));
      } catch {
        const direct = this.querySelector('[data-ucp-feature="directCheckout"]'); if (direct) direct.hidden = true;
        this.showError('This configuration is not available yet. Request a quote and our team can help.');
        return;
      }
      this.money = (cents) => `${new Intl.NumberFormat(this.dataset.locale || 'en-US', { style: 'currency', currency: this.config.currency, minimumFractionDigits: cents % 100 ? 2 : 0, maximumFractionDigits: 2 }).format(cents / 100)} ${this.config.currency}`;
      this.unitMoney = (quote) => `${new Intl.NumberFormat(this.dataset.locale || 'en-US', { style: 'currency', currency: this.config.currency, minimumFractionDigits: 3, maximumFractionDigits: 3 }).format(quote.cents / quote.quantity / 100)} ${this.config.currency}`;
      this.nativeMode = this.config.mode === 'native';
      this.orderQuantity = 1;
      this.quantityInput = this.querySelector('[data-ucp-quantity-input]');
      this.buildOptionControls();
      this.applyPageSettings();
      this.setupPickers();
      this.optionSelects.forEach((select, i) => this.listen(select, 'change', () => this.select({values: {...this.selection?.values, [this.config.options[i].id]: select.value}})));
      this.listen(this.quantityInput, 'input', () => { this.orderQuantity = Number(this.quantityInput.value); this.updateSummary(); this.updateProperties(); });
      if (this.config.page.features.artwork) this.setupArtwork();
      if (this.config.page.features.sticky) this.setupSticky();
      this.setupDelivery();
      if (this.config.page.features.reviews) this.setupReviews();
      this.listen(this.form, 'submit', (event) => this.addToCart(event));
      const initialVariant = new URLSearchParams(location.search).get('variant');
      const initial = this.config.quotes.find((quote) => String(quote.variantId) === initialVariant) || this.config.quotes[0];
      this.select(initial || {}, true);
      const steps = this.querySelector('[data-ucp-proof-steps]');
      if (this.config.service?.steps?.length) steps.replaceChildren(...this.config.service.steps.map((step) => element('li', '', step)));
      const days = this.config.delivery?.production;
      if (days && this.config.page.features.delivery) this.querySelector('[data-ucp-production]').textContent = `${days.min}–${days.max} working days`;
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.priceRequest?.abort();
      this.termsRequest?.abort();
      this.visibilityObserver?.disconnect();
      this.xhr?.abort();
      this.sticky?.remove();
      if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
      document.body.classList.remove('ucp-sticky-visible');
    }

    populate(select, options, value, placeholder) {
      const entries = options.length ? options : [{ id: 'none', label: placeholder || 'Not applicable' }];
      select.replaceChildren(...entries.map((entry) => {
        const option = element('option', '', entry.label);
        option.value = entry.id;
        return option;
      }));
      select.value = value;
    }

    buildOptionControls() {
      const container = this.querySelector('[data-ucp-option-controls]');
      container.replaceChildren(...this.config.options.map((option, index) => {
        const row = element('label', 'ucp-select-row');
        const select = document.createElement('select');
        select.dataset.ucpChoice = String(index);
        select.setAttribute('aria-label', option.label);
        const arrow = element('span', '', '⌄'); arrow.setAttribute('aria-hidden', 'true');
        row.append(element('strong', '', option.label), select, arrow);
        return row;
      }));
      this.optionSelects = [...container.querySelectorAll('[data-ucp-choice]')];
    }

    applyPageSettings() {
      const {page} = this.config;
      this.dataset.orderMode = this.config.mode;
      this.querySelectorAll('[data-ucp-feature]').forEach(node => { node.hidden = !page.features[node.dataset.ucpFeature]; });
      this.querySelector('[data-ucp-native-quantity]').hidden = !this.nativeMode;
      this.querySelector('[data-ucp-batch-quantity]').hidden = this.nativeMode;
      this.querySelector('[data-ucp-batch-note]').hidden = this.nativeMode;
      this.querySelector('[data-ucp-quantity-label]').textContent = page.quantityLabel || 'Quantity';
      if (page.eyebrow) this.querySelector('.ucp-eyebrow').textContent = page.eyebrow;
      if (page.quoteText) this.querySelector('[data-ucp-quote] strong').textContent = page.quoteText;
      this.querySelector('[data-ucp-add-note]').hidden = !page.features.artwork;
      const required = page.features.artwork && !!this.config.upload?.required;
      const requirement = this.querySelector('[data-ucp-upload-requirement]');
      requirement.textContent = required ? requirement.dataset.requiredText : requirement.dataset.optionalText;
      this.later.closest('label').hidden = required;
      if (required) this.querySelector('[data-ucp-add-note]').textContent = requirement.dataset.requiredText;
      this.querySelector('[data-ucp-rating-link]').hidden = !page.features.reviews;
      this.querySelector('[data-ucp-option-help]').hidden = true;
      this.querySelector('[data-ucp-gallery-badge]').hidden = this.nativeMode && !page.features.artwork && !page.features.proof;
    }

    setupPickers() {
      this.pickers = this.optionSelects.map((select, index) => {
        const option = this.config.options[index];
        select.closest('.ucp-select-row').hidden = !option;
        if (!option) return null;
        select.setAttribute('aria-label', option.label);
        select.closest('label').querySelector('strong').textContent = option.label;
        return new ProductOptionPicker({select, kind: option.kind || 'generic', id:`${this.dataset.section}-${index}`, listen:this.listen,
          onOpen:(current) => this.pickers.forEach(picker => { if (picker && picker !== current) picker.close(); })});
      });
      this.listen(document, 'pointerdown', (event) => this.pickers.forEach(picker => { if (picker && !picker.host.contains(event.target)) picker.close(); }));
      this.listen(window, 'resize', () => this.pickers.forEach(picker => picker?.close()));
    }

    select(changes, initial = false) {
      if (this.busy) return;
      if (this.nativeMode) { this.selectNative(changes.values, initial).catch(error => { if (error.name !== 'AbortError') this.showError(error.message); }); return; }
      const next = selectBatch(this.config, {...this.selection, ...changes});
      if (!next) return;
      this.selection = next;
      this.verified = null;
      this.syncOptions(next.states);
      this.querySelector('[data-ucp-moq]').textContent = `From ${next.quotes[0].quantity.toLocaleString()} pcs`;
      this.renderQuotes();
      this.updateSummary();
      this.updateProperties();
      if (/^\d+$/.test(String(next.quote.variantId))) {
        const url = new URL(location.href); url.searchParams.set('variant', next.quote.variantId);
        history.replaceState({}, '', url.pathname + url.search + url.hash);
      }
      this.showError('');
      this.querySelector('[data-ucp-order-success]').hidden = true;
      this.verifySelection().catch(() => {});
      this.updatePaymentTerms();
    }

    syncOptions(states) {
      states.forEach((state, i) => {
        this.populate(this.optionSelects[i], state.values, state.value);
        this.optionSelects[i].disabled = this.busy || state.active === false;
        this.pickers[i]?.sync(state.values, state.value);
      });
    }

    async selectNative(values, initial) {
      const selected = nativeQuote(this.config.native);
      const requested = values || selected?.values || {};
      this.selection = {values: requested, quote: selected, quantity: 1, quotes: []};
      this.verified = null;
      this.syncOptions(this.config.options.map(option => ({...option, value: requested[option.id]})));
      this.updateButton();
      if (!initial) {
        const version = ++this.requestVersion;
        this.priceRequest?.abort(); this.priceRequest = new AbortController();
        const url = this.productContextUrl();
        url.searchParams.delete('variant'); url.searchParams.set('option_values', this.config.options.map(option => requested[option.id]).join(','));
        url.searchParams.set('section_id', 'usayg-custom-variant-state');
        const response = await fetch(url, {signal: this.priceRequest.signal});
        if (!response.ok) throw new Error('Could not load the selected option. Please try again.');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const state = JSON.parse(html.querySelector('[data-ucp-variant-state]')?.textContent || 'null');
        if (version !== this.requestVersion) return;
        if (!state?.native) throw new Error('This option combination is unavailable.');
        this.config = productConfiguration(this.config, state.native);
        this.selection = {values: requested, quote: nativeQuote(state.native), quantity: 1, quotes: []};
      }
      const quote = this.selection.quote;
      if (!quote || Object.entries(requested).some(([key, value]) => quote.values[key] !== value)) {
        this.selection.quote = null; this.updateButton();
        this.querySelector('[data-ucp-selected-total]').textContent = '';
        this.showError('This option combination is unavailable.'); return;
      }
      this.syncOptions(this.config.options.map(option => ({...option, value: this.selection.values[option.id]})));
      const rule = quote.quantityRule || {min: 1, increment: 1};
      this.quantityInput.min = rule.min; this.quantityInput.step = rule.increment;
      if (rule.max) this.quantityInput.max = rule.max; else this.quantityInput.removeAttribute('max');
      this.orderQuantity = normalizeOrderQuantity(this.orderQuantity, rule);
      this.quantityInput.value = this.orderQuantity;
      this.updateProperties(); this.updateSummary(); this.showError('');
      const url = new URL(location.href); url.searchParams.set('variant', quote.variantId); url.searchParams.delete('option_values');
      history.replaceState({}, '', url.pathname + url.search + url.hash);
      await this.verifySelection(); this.updatePaymentTerms();
    }

    renderQuotes() {
      const { quotes, quote: selected } = this.selection;
      const rows = quotes.map((quote) => {
        const row = element('tr', quote.quantity === selected.quantity ? 'is-selected' : '');
        const quantityCell = element('td');
        const label = element('label', 'ucp-quantity-choice');
        const input = document.createElement('input');
        input.type = 'radio'; input.name = `batch-${this.dataset.section}`; input.value = String(quote.quantity);
        input.checked = quote.quantity === selected.quantity;
        input.setAttribute('aria-label', `${quote.quantity.toLocaleString()} pieces, ${this.money(quote.cents)}`);
        this.listen(input, 'change', () => this.select({ quantity: quote.quantity }));
        label.append(input, element('span', '', quote.quantity.toLocaleString())); quantityCell.append(label);
        const save = element('td'); const percentage = savingsPercent(quote, quotes[0]);
        save.append(element('span', 'ucp-saving', percentage ? `-${percentage}%` : '0%'));
        const total = element('td', 'ucp-quote-total', this.money(quote.cents));
        const unit = element('td', 'ucp-quote-unit'); unit.append(element('span', '', this.unitMoney(quote)), element('small', '', '/ Unit'));
        row.append(quantityCell, save, total, unit);
        this.listen(row, 'click', (event) => { if (!event.target.closest('input, label')) this.select({ quantity: quote.quantity }); });
        return row;
      });
      this.querySelector('[data-ucp-quote-rows]').replaceChildren(...rows);
    }

    updateSummary() {
      const quote = this.selection?.quote;
      if (!quote) return;
      const total = this.nativeMode ? unitPrice(quote, this.orderQuantity) * this.orderQuantity : quote.cents;
      this.querySelector('[data-ucp-selected-total]').textContent = this.money(Number.isSafeInteger(total) && total >= 0 ? total : quote.cents);
      const compare = this.querySelector('[data-ucp-compare-price]');
      compare.hidden = !this.nativeMode || !(quote.compareAtPrice > unitPrice(quote, this.orderQuantity));
      compare.textContent = compare.hidden ? '' : this.money(quote.compareAtPrice * this.orderQuantity);
      this.querySelector('[data-ucp-selected-batch]').textContent = this.nativeMode ? `${this.orderQuantity || 0} × ${this.unitMoney({cents: unitPrice(quote, this.orderQuantity), quantity: 1})}` : `${quote.quantity.toLocaleString()} pieces / batch`;
      this.querySelector('[data-ucp-selected-sku]').textContent = quote.sku ? `SKU: ${quote.sku}` : '';
      if (this.sticky) this.sticky.querySelector('[data-ucp-sticky-price]').textContent = this.money(Number.isSafeInteger(total) && total >= 0 ? total : quote.cents);
      this.updateButton();
    }

    updateProperties() {
      const selection = this.selection;
      if (!selection?.quote) return;
      const labels = this.config.options.map(option => ({label: option.label, value: option.values.find(value => value.id === selection.values[option.id])?.label || 'Not applicable'}));
      const properties = this.querySelector('[data-ucp-properties]');
      properties.replaceChildren(...(this.nativeMode ? [] : labels.map(({label, value}) => { const input = document.createElement('input'); input.type = 'hidden'; input.name = `properties[${label}]`; input.value = value; return input; })));
      if (!this.nativeMode) {
        for (const [name, value] of [['Pieces per batch', selection.quantity], ['Selling unit', `${selection.quantity.toLocaleString()} pieces / batch`]]) {
          const input = document.createElement('input'); input.type = 'hidden'; input.name = `properties[${name}]`; input.value = value; properties.append(input);
        }
      }
      this.querySelector('[data-ucp-variant]').value = selection.quote.variantId || '';
      this.querySelector('[data-ucp-cart-quantity]').value = this.nativeMode ? this.orderQuantity : 1;
      this.querySelector('[data-ucp-variant]').dispatchEvent(new Event('change', {bubbles: true}));
      const target = new URL(this.dataset.quoteUrl, location.origin);
      target.searchParams.set('product', this.dataset.productTitle);
      target.searchParams.set('variant', labels.map(value => value.value).join(' / '));
      if (selection.quote.sku) target.searchParams.set('sku', selection.quote.sku);
      target.searchParams.set('quantity', this.nativeMode ? String(this.orderQuantity) : `${selection.quantity.toLocaleString()} pieces / 1 batch`);
      this.querySelector('[data-ucp-quote]').href = target.pathname + target.search;
    }

    async verifySelection(force = false) {
      const quote = this.selection.quote;
      const id = String(quote.variantId || '');
      const version = ++this.requestVersion;
      this.priceRequest?.abort();
      if (!/^\d+$/.test(id)) { this.updateButton(); return null; }
      if (this.dataset.currency !== this.config.currency) {
        this.showError(`This custom quote is available in ${this.config.currency}. Request a quote for another currency.`);
        return null;
      }
      const cached = this.priceCache.get(id);
      if (!force && cached && Date.now() - cached.time < 60000) {
        if (!this.matchesNativeOptions(cached.state, quote) || (!this.nativeMode && cached.state.price !== quote.cents)) { this.verified = null; this.showError('This batch configuration or price has changed. Request a quote for help.'); this.updateButton(); return null; }
        this.acceptVariant(cached.state, version); return cached.state;
      }
      this.priceRequest = new AbortController();
      try {
        const url = this.productContextUrl();
        url.searchParams.set('variant', id); url.searchParams.set('section_id', 'usayg-custom-variant-state');
        const response = await fetch(url, { signal: this.priceRequest.signal });
        if (!response.ok) throw new Error('We could not check this configuration. Try again or request a quote.');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const state = JSON.parse(html.querySelector('[data-ucp-variant-state]')?.textContent || 'null');
        if (!state || String(state.id) !== id || state.currency !== this.config.currency) throw new Error('This batch is not available for online ordering. Request a quote for help.');
        if (!this.matchesNativeOptions(state, quote)) throw new Error('This batch configuration has changed. Request a quote for help.');
        if (!this.nativeMode && state.price !== quote.cents) throw new Error('This batch price has changed. Request a quote for the updated price.');
        this.priceCache.set(id, { state, time: Date.now() });
        this.acceptVariant(state, version);
        return state;
      } catch (error) {
        if (version === this.requestVersion && error.name !== 'AbortError') { this.verified = null; this.showError(error.message); this.updateButton(); }
        if (force) throw error;
        return null;
      }
    }

    matchesNativeOptions(state, quote) {
      if (this.nativeMode || !quote.nativeOptions) return true;
      const actual = (state.native?.options || []).map(option => ({name: option.label,
        value: option.values.find(value => String(value.id) === String(state.native?.selected?.values?.[option.id]))?.label}));
      return Array.isArray(quote.nativeOptions) && quote.nativeOptions.length > 0 && new Set(quote.nativeOptions.map(option => option.name)).size === quote.nativeOptions.length && actual.length === quote.nativeOptions.length && quote.nativeOptions.every(expected => actual.some(option => option.name === expected.name && option.value === expected.value));
    }

    acceptVariant(state, version) {
      if (version !== this.requestVersion || String(state.id) !== String(this.selection.quote.variantId)) return;
      this.verified = state;
      if (this.nativeMode && state.native?.selected) {
        this.selection.quote.quantityRule = state.native.selected.quantityRule;
        this.selection.quote.priceBreaks = state.native.selected.priceBreaks;
        this.selection.quote.compareAtPrice = state.native.selected.compareAtPrice;
      }
      if (state.sku) this.selection.quote.sku = state.sku;
      if (Number.isSafeInteger(state.price) && this.selection.quote.cents !== state.price) {
        this.selection.quote.cents = state.price;
        this.renderQuotes(); this.updateSummary();
      }
      if (!state.available) this.showError('This selection is currently unavailable. Choose another option or request a quote.');
      this.updateSummary();
      this.updateButton();
      const mediaId = state.native?.selected?.mediaId;
      if (mediaId) {
        const index = [...this.querySelectorAll('[data-ucp-slide]')].findIndex(slide => slide.dataset.mediaId === mediaId);
        if (index >= 0) this.querySelectorAll('[data-ucp-image]')[index]?.click();
      }
    }

    productContextUrl() {
      if (location.pathname.includes('products_preview') && new URLSearchParams(location.search).has('preview_key')) return new URL(location.href);
      const url = new URL(this.dataset.productUrl, location.origin), context = new URLSearchParams(location.search);
      for (const key of ['preview_theme_id', 'view', 'pb']) if (context.has(key)) url.searchParams.set(key, context.get(key));
      return url;
    }

    async updatePaymentTerms() {
      const id = this.selection.quote.variantId;
      const target = this.querySelector('[data-ucp-payments]');
      this.termsRequest?.abort();
      target.hidden = true;
      if (!/^\d+$/.test(String(id)) || !target.querySelector('shopify-payment-terms')) return;
      this.termsRequest = new AbortController();
      try {
        const url = this.productContextUrl();
        url.searchParams.set('variant', id); url.searchParams.set('section_id', this.dataset.section);
        const response = await fetch(url, { signal: this.termsRequest.signal });
        if (!response.ok) return;
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const source = html.querySelector('[data-ucp-payments]');
        if (String(this.selection.quote.variantId) === String(id) && source) { target.replaceChildren(...source.childNodes); target.hidden = false; }
      } catch { /* Native payment terms stay hidden when a current offer cannot be loaded. */ }
    }

    updateButton() {
      if (!this.addButton) return;
      const enabled = !!this.selection?.quote && !!this.verified?.available && !this.busy && !this.fileError && (!this.nativeMode || orderQuantity(this.orderQuantity, this.selection.quote.quantityRule));
      this.addButton.disabled = !enabled;
      const direct = this.querySelector('[data-ucp-feature="directCheckout"]');
      if (direct) direct.hidden = !this.config.page.features.directCheckout || !enabled;
      this.addButton.querySelector('strong').textContent = this.busy ? 'Adding to cart…' : !this.selection?.quote ? 'Unavailable' : !this.verified ? 'Checking availability…' : this.verified.available ? 'Add to cart' : 'Unavailable';
      if (this.sticky) this.sticky.querySelector('[data-ucp-sticky-add]').disabled = !enabled;
    }

    showError(message) {
      const target = this.querySelector('[data-ucp-order-error]');
      target.textContent = message; target.hidden = !message;
    }

    setupArtwork() {
      const maxBytes = this.config.upload?.maxBytes || 10 * 1024 * 1024;
      this.querySelector('[data-ucp-file-limit]').textContent = `Up to ${Math.round(maxBytes / 1024 / 1024)} MB. Your file uploads when you add this item to cart.`;
      this.listen(this.fileInput, 'change', () => this.readArtwork());
      this.listen(this.querySelector('[data-ucp-remove-file]'), 'click', () => this.removeArtwork());
      this.listen(this.later, 'change', () => { if (this.later.checked) this.removeArtwork(); else this.fileInput.click(); });
      const zone = this.querySelector('[data-ucp-dropzone]');
      this.listen(zone, 'dragover', (event) => { event.preventDefault(); if (!this.busy) zone.classList.add('is-dragging'); });
      this.listen(zone, 'dragleave', () => zone.classList.remove('is-dragging'));
      this.listen(zone, 'drop', (event) => {
        event.preventDefault(); zone.classList.remove('is-dragging');
        if (this.busy || !event.dataTransfer.files.length) return;
        const transfer = new DataTransfer(); transfer.items.add(event.dataTransfer.files[0]); this.fileInput.files = transfer.files; this.readArtwork();
      });
    }

    readArtwork() {
      this.querySelector('[data-ucp-order-success]').hidden = true;
      const file = this.fileInput.files[0];
      const error = validateArtwork(file, this.config.upload?.maxBytes);
      if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
      this.fileError = error && this.config.upload?.required ? error : '';
      const feedback = this.querySelector('[data-ucp-file-error]'); feedback.textContent = error; feedback.hidden = !error;
      const preview = this.querySelector('[data-ucp-file-preview]'); preview.hidden = true;
      this.querySelector('[data-ucp-file]').hidden = !file || !!error;
      if (error) { this.fileInput.value = ''; this.later.checked = true; }
      else if (file) {
        this.later.checked = false;
        this.querySelector('[data-ucp-file-name]').textContent = file.name;
        this.querySelector('[data-ucp-file-status]').textContent = `${(file.size / 1024).toFixed(0)} KB · Ready to upload with your order`;
        if (file.type === 'image/png') { this.previewUrl = URL.createObjectURL(file); preview.src = this.previewUrl; preview.hidden = false; }
      }
      this.querySelector('[data-ucp-property="artwork"]').value = file && !error ? `Uploaded with order: ${file.name}` : 'Email after ordering';
      this.updateButton();
    }

    removeArtwork() {
      if (this.busy) return;
      this.fileInput.value = ''; this.fileError = ''; this.later.checked = true;
      if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
      this.querySelector('[data-ucp-file]').hidden = true;
      this.querySelector('[data-ucp-file-error]').hidden = true;
      this.querySelector('[data-ucp-property="artwork"]').value = 'Email after ordering';
      this.updateButton();
    }

    setBusy(busy) {
      this.busy = busy;
      this.optionSelects.forEach((select, i) => { select.disabled = busy || this.selection.states?.[i]?.active === false; });
      this.quantityInput.disabled = busy;
      this.fileInput.disabled = busy; this.later.disabled = busy;
      this.pickers.forEach(picker => picker?.setDisabled(picker.select.disabled));
      this.querySelectorAll('[data-ucp-quote-rows] input').forEach((input) => input.disabled = busy);
      this.updateButton();
    }

    async addToCart(event) {
      event.preventDefault();
      if (this.busy || !this.selection?.quote || this.fileError) return;
      if (this.nativeMode && !orderQuantity(this.orderQuantity, this.selection.quote.quantityRule)) { this.showError('Choose a valid order quantity.'); return; }
      const file = this.config.page.features.artwork ? this.fileInput.files[0] : null;
      if (this.config.page.features.artwork && this.config.upload?.required && !file) { this.fileError = 'Choose an artwork file before adding this order.'; this.querySelector('[data-ucp-file-error]').textContent = this.fileError; this.querySelector('[data-ucp-file-error]').hidden = false; this.updateButton(); return; }
      this.showError('');
      this.querySelector('[data-ucp-order-success]').hidden = true;
      const claimedPrice = this.selection.quote.cents;
      const claimedTotal = this.nativeMode ? unitPrice(this.selection.quote, this.orderQuantity) * this.orderQuantity : claimedPrice;
      this.setBusy(true);
      const progress = this.querySelector('[data-ucp-upload-progress]');
      try {
        const state = await this.verifySelection(true);
        if (!state?.available) throw new Error('This batch is currently unavailable. Request a quote for help.');
        if (this.nativeMode && !orderQuantity(this.orderQuantity, this.selection.quote.quantityRule)) throw new Error('The quantity rules have changed. Review the selected quantity and try again.');
        if (state.price !== claimedPrice) throw new Error(`The price has changed to ${this.money(state.price)}. Review it and add it again.`);
        if (this.nativeMode && unitPrice(this.selection.quote, this.orderQuantity) * this.orderQuantity !== claimedTotal) throw new Error('The price has changed. Review the updated total and add it again.');
        this.updateProperties();
        const requestField = this.querySelector('[data-ucp-request-id]');
        requestField.value = crypto.randomUUID();
        // Include file controls before temporarily disabling them for the request.
        this.fileInput.disabled = false;
        const formData = new FormData(this.form);
        formData.set('quantity', String(this.nativeMode ? this.orderQuantity : 1));
        if (!this.config.page.features.artwork) { formData.delete('properties[Artwork status]'); formData.delete('properties[_Design request]'); }
        if (!this.config.page.features.proof) formData.delete('properties[Artwork proof]');
        this.fileInput.disabled = true;
        if (!file) formData.delete('properties[Artwork]');
        const cart = document.querySelector('cart-notification, cart-drawer');
        if (cart?.getSectionsToRender) {
          formData.append('sections', cart.getSectionsToRender().map((section) => section.id).join(','));
          formData.append('sections_url', location.pathname); cart.setActiveElement?.(this.addButton);
        }
        progress.hidden = !file; progress.value = 0;
        const response = await new Promise((resolve, reject) => {
          this.xhr = new XMLHttpRequest();
          this.xhr.open('POST', `${window.Shopify?.routes?.root || '/'}cart/add.js`);
          this.xhr.setRequestHeader('Accept', 'application/json'); this.xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
          this.xhr.timeout = 120000;
          this.xhr.upload.onprogress = (update) => { if (update.lengthComputable) progress.value = Math.round(update.loaded / update.total * 100); };
          this.xhr.onerror = () => reject(new Error('The connection was interrupted. Check your cart before trying again.'));
          this.xhr.ontimeout = () => reject(new Error('The upload timed out. Check your cart before trying again.'));
          this.xhr.onabort = () => reject(new Error('The upload was cancelled.'));
          this.xhr.onload = () => {
            let result; try { result = JSON.parse(this.xhr.responseText); } catch { reject(new Error('We could not read the cart response. Check your cart before trying again.')); return; }
            if (this.xhr.status >= 400 || result.status) reject(new Error(typeof result.description === 'string' ? result.description : 'This batch could not be added. Please try again or request a quote.'));
            else resolve(result);
          };
          this.xhr.send(formData);
        });
        if (file) {
          const uploaded = response.properties?.Artwork;
          this.querySelector('[data-ucp-file-status]').textContent = typeof uploaded === 'string' && /^https?:\/\//.test(uploaded) ? 'Uploaded and attached to your cart item' : 'Added to cart — review the artwork attachment in your cart';
        }
        const success = this.querySelector('[data-ucp-order-success]'); success.replaceChildren(document.createTextNode(this.nativeMode ? `${this.orderQuantity} added to cart. ` : `${this.selection.quantity.toLocaleString()} pieces added as 1 production batch. `));
        const link = element('a', '', 'View cart'); link.href = `${window.Shopify?.routes?.root || '/'}cart`; success.append(link); success.hidden = false;
        if (cart?.renderContents && response.sections) cart.renderContents(response);
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'usayg-custom-product', productVariantId: state.id, cartData: response });
        this.dispatchEvent(new CustomEvent('usayg:custom-added', { bubbles: true, detail: { variantId: state.id, pieces: this.selection.quantity } }));
      } catch (error) {
        this.showError(error.message || 'The batch could not be added. Please try again.');
      } finally {
        progress.hidden = true; this.setBusy(false);
      }
    }

    setupTabs() {
      const tabs = [...this.querySelectorAll('[data-ucp-tab]')]; const panels = [...this.querySelectorAll('[data-ucp-panel]')];
      const select = (index) => tabs.forEach((tab, i) => { tab.setAttribute('aria-selected', String(index === i)); tab.tabIndex = index === i ? 0 : -1; panels[i].hidden = i !== index; });
      tabs.forEach((tab, index) => {
        this.listen(tab, 'click', () => select(index));
        this.listen(tab, 'keydown', (event) => {
          const next = event.key === 'ArrowRight' ? (index + 1) % tabs.length : event.key === 'ArrowLeft' ? (index + tabs.length - 1) % tabs.length : event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : null;
          if (next !== null) { event.preventDefault(); select(next); tabs[next].focus(); }
        });
      });
      select(0);
    }

    setupSticky() {
      this.sticky = this.querySelector('[data-ucp-sticky]'); document.body.append(this.sticky);
      this.listen(this.sticky.querySelector('[data-ucp-sticky-add]'), 'click', () => this.form.requestSubmit(this.addButton));
      this.listen(this.sticky.querySelector('[data-ucp-sticky-options]'), 'click', () => { this.querySelector('.ucp-options').scrollIntoView({ block: 'start' }); (this.pickers.find(Boolean)?.button || this.quantityInput).focus({ preventScroll: true }); });
      this.visibilityObserver = new IntersectionObserver(([entry]) => { this.sticky.hidden = entry.isIntersecting || entry.boundingClientRect.bottom >= 0; document.body.classList.toggle('ucp-sticky-visible', !this.sticky.hidden); });
      this.visibilityObserver.observe(this.addButton);
    }

    setupDelivery() {
      const defaults = this.querySelector('[data-ucp-default-delivery]');
      const delivery = this.config.page.features.delivery ? this.config.delivery : defaults ? JSON.parse(defaults.textContent) : null;
      const windows = deliveryWindows({delivery});
      if (!windows.length) return;
      const format = new Intl.DateTimeFormat(this.dataset.locale || 'en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
      this.querySelector('[data-ucp-delivery]').hidden = false;
      this.querySelector('[data-ucp-delivery-windows]').replaceChildren(...windows.map((window) => {
        const note = window.note || (delivery.dayType === 'calendar' ? `${window.days.min}–${window.days.max} ${this.querySelector('[data-ucp-delivery]').dataset.calendarDays || 'calendar days'}` : '');
        const row = element('div', 'ucp-delivery__row'); row.append(element('span', '', window.label), element('strong', '', `${format.format(window.earliest)} – ${format.format(window.latest)}`), element('small', '', note)); return row;
      }));
      this.querySelector('[data-ucp-delivery-note]').textContent = delivery.dayType === 'calendar'
        ? this.querySelector('[data-ucp-delivery-note]').dataset.calendarNote || 'Estimated from order confirmation, in calendar days. Shipping options and charges are confirmed at checkout or in your quote.'
        : `${delivery.example ? 'Example schedule. ' : ''}Assumes artwork approval today. Working days exclude weekends and configured holidays. Shipping and taxes are confirmed at checkout or in your quote.`;
    }

    setupReviews() {
      let reviews = [];
      try { reviews = publishedReviews(JSON.parse(this.querySelector('[data-ucp-reviews]').textContent)); } catch { /* No published reviews yet. */ }
      const count = reviews.length; const average = count ? reviews.reduce((sum, review) => sum + review.rating, 0) / count : 0;
      const ratingLink = this.querySelector('[data-ucp-rating-link]'); ratingLink.textContent = count ? `${'★'.repeat(Math.round(average))} ${average.toFixed(1)} (${count} ${count === 1 ? 'review' : 'reviews'}) · Read reviews` : 'Be the first to review';
      const summary = this.querySelector('[data-ucp-review-summary]'); summary.textContent = count ? `${average.toFixed(1)} out of 5 · Based on ${count} published ${count === 1 ? 'review' : 'reviews'}` : 'No reviews yet. Share your experience with this product.';
      const filters = this.querySelector('[data-ucp-review-filters]'); filters.hidden = !count;
      let limit = 5;
      const render = () => {
        const rating = this.querySelector('[data-ucp-review-rating]').value;
        const sort = this.querySelector('[data-ucp-review-sort]').value;
        const filtered = publishedReviews(reviews, { rating, sort });
        this.querySelector('[data-ucp-review-list]').replaceChildren(...filtered.slice(0, limit).map((review) => {
          const card = element('article', 'ucp-review'); const heading = element('div', 'ucp-review__heading');
          const stars = element('span', 'ucp-review__stars', '★'.repeat(review.rating)); stars.setAttribute('aria-label', `${review.rating} out of 5 stars`);
          heading.append(stars, element('time', '', review.date || '')); card.append(heading, element('h3', '', review.title || ''), element('strong', '', review.author), element('p', '', review.text)); return card;
        }));
        this.querySelector('[data-ucp-more-reviews]').hidden = filtered.length <= limit;
      };
      this.listen(this.querySelector('[data-ucp-review-rating]'), 'change', () => { limit = 5; render(); });
      this.listen(this.querySelector('[data-ucp-review-sort]'), 'change', render);
      this.listen(this.querySelector('[data-ucp-more-reviews]'), 'click', () => { limit += 5; render(); });
      if (count) {
        const histogram = element('div', 'ucp-review-histogram');
        for (let stars = 5; stars >= 1; stars--) {
          const number = reviews.filter((review) => review.rating === stars).length;
          const button = element('button', '', `${stars} stars`); button.type = 'button';
          const bar = element('span', 'ucp-review-histogram__bar'); const fill = element('span'); fill.style.width = `${number / count * 100}%`; bar.append(fill);
          button.append(bar, element('small', '', `${Math.round(number / count * 100)}% (${number})`));
          button.setAttribute('aria-label', `Show ${number} reviews with ${stars} stars`);
          this.listen(button, 'click', () => { this.querySelector('[data-ucp-review-rating]').value = String(stars); limit = 5; render(); });
          histogram.append(button);
        }
        summary.append(histogram);
      }
      const form = this.querySelector('[data-ucp-review-form]');
      this.listen(this.querySelector('[data-ucp-write-review]'), 'click', () => { form.hidden = !form.hidden; if (!form.hidden) { form.scrollIntoView({ block: 'start' }); form.querySelector('input:not([type="hidden"])')?.focus({ preventScroll: true }); } });
      if (new URLSearchParams(location.search).get('contact_posted') === 'true' || form.querySelector('.errors')) form.hidden = false;
      render();
    }
  });
}
